import { cn } from "@app/lib/utils";
import { URLS } from "@app/shared";
import { Link } from "@design-system/components";
import {
  investorQuickLinks,
  quarterlyResultsLinks,
} from "@modules/investor-relations/data";
import { trans } from "@mongez/localization";
import type { LucideIcon } from "lucide-react";
import { ChevronRight, FileSpreadsheet, FileText } from "lucide-react";
import type { ReactNode } from "react";

const quarterlyIcons: LucideIcon[] = [FileText, FileSpreadsheet];

/**
 * `index` drives the leading rule: at `sm` the grid is 2-up so only the second
 * column of each row gets one; from `xl` it is 4-up so every column but the
 * first does.
 */
function Column({
  title,
  index,
  children,
}: {
  title: string;
  index: number;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex h-full flex-col gap-6",
        index % 2 === 1 && "sm:border-s sm:border-white/10 sm:ps-6",
        index > 0 && "xl:border-s xl:border-white/10 xl:ps-8",
      )}
    >
      <h3 className="text-xl font-bold text-white sm:text-2xl">{title}</h3>
      {children}
    </div>
  );
}

/**
 * Investor Information — Section 11 of the approved IR content sheet.
 * Merges Latest Quarterly Results, Financial Calendar, Share Information and
 * Quick Links into the single dark band shown in the approved design.
 */
export function InvestorInformation({ standard }: { standard: string }) {
  return (
    <section
      aria-label={trans("investorInformation.ariaLabel")}
      className="bg-ir-band py-14 lg:py-20"
    >
      <div className="container grid items-stretch gap-x-10 gap-y-12 sm:grid-cols-2 2xl:grid-cols-3">
        <Column index={0} title={trans("latestQuarterlyResults.title")}>
          {/* The calendar column sets the band height; the other three spread
              their content down it rather than leaving a ragged bottom edge. */}
          <nav className="flex flex-col gap-4">
            {quarterlyResultsLinks.map(({ label, href }, index) => {
              const Icon = quarterlyIcons[index] ?? FileText;

              return (
                <Link
                  key={label}
                  to={href}
                  className="group flex flex-1 items-center gap-3 rounded-xl border border-white/15 bg-white/[0.06] px-5 py-5 transition-colors hover:border-white/35 hover:bg-white/10"
                >
                  <Icon
                    className="size-5 shrink-0 text-white/70"
                    strokeWidth={1.6}
                    aria-hidden
                  />
                  <span className="flex-1 text-base font-semibold text-white">
                    {trans(label)}
                  </span>
                  <ChevronRight
                    className="size-4 shrink-0 text-white/45 transition-colors group-hover:text-white rtl:rotate-180"
                    strokeWidth={2}
                    aria-hidden
                  />
                </Link>
              );
            })}
          </nav>
        </Column>

        {/* <Column index={1} title={trans("financialCalendar")}>
          <div className="min-h-[9rem] flex-1 overflow-hidden rounded-xl bg-white px-3 py-1 [&_iframe]:h-full [&_iframe]:w-full">
            <euroland-tool
              data-tool-name="FinCalendar2"
              data-tool-version="ticker"
              data-tool-language={standard}
            />
          </div>
          <Link
            to={URLS.investor.financialCalendar}
            className="group inline-flex w-fit items-center gap-2 text-xs font-bold uppercase tracking-wider text-white transition-opacity hover:opacity-80"
          >
            {trans("viewAllEvents")}
            <ChevronRight
              className="size-4 rtl:rotate-180"
              strokeWidth={2}
              aria-hidden
            />
          </Link>
        </Column> */}

        <Column index={1} title={trans("shareInformation")}>
          <p className="text-base leading-loose text-white/70 sm:text-[1.0625rem]">
            {trans("shareholderInformation.body")}
          </p>
          <Link
            to={URLS.investor.shareInformation}
            className="inline-flex w-fit items-center gap-2 rounded-full bg-bright-orange px-7 py-3 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-steel"
          >
            {trans("shareholderInformation.readMore")}
            <ChevronRight
              className="size-4 rtl:rotate-180"
              strokeWidth={2}
              aria-hidden
            />
          </Link>
        </Column>

        <Column index={2} title={trans("quickLinks")}>
          <ul className="flex flex-1 flex-col">
            {investorQuickLinks.map(({ label, href }) => (
              <li
                key={label}
                className="flex flex-1 border-b border-white/10 last:border-0"
              >
                <Link
                  to={href}
                  className="group flex w-full items-center justify-between gap-3 py-4 text-base text-white/80 transition-colors hover:text-white"
                >
                  {trans(label)}
                  <ChevronRight
                    className="size-4 shrink-0 text-white/40 transition-colors group-hover:text-bright-orange rtl:rotate-180"
                    strokeWidth={2}
                    aria-hidden
                  />
                </Link>
              </li>
            ))}
          </ul>
        </Column>
      </div>
    </section>
  );
}
